import CloseRounded from "@mui/icons-material/CloseRounded";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import React, { useState } from "react";
import { kickNavy } from "~/shared/styles/baseStyles";
import {
  medicationHeadingStyles,
  medicationInputStyles,
  medicationPrimaryButtonStyles,
  medicationSecondaryButtonStyles,
} from "./medicationReviewStyles";

// zIndex lifts the drawer above an open dialog (the EMR medication editor); a
// MUI drawer otherwise sits below dialogs.
export default function MedicationDetails({
  entry,
  isNew,
  onSave,
  onRemove,
  onClose,
  zIndex,
}) {
  const [draft, setDraft] = useState({ ...entry });
  const name = (draft.name || "").trim();
  const sameMedication = name === (entry.name || "").trim();
  const productDetails = [
    entry.productStrength && `Strength on the label: ${entry.productStrength}`,
    entry.form,
    entry.route,
  ].filter(Boolean);

  const save = (event) => {
    event.preventDefault();
    if (!name) return;
    // A renamed medication no longer has the old product's identifiers or
    // formulation. Dose and frequency edits do not change product identity.
    onSave({
      ...draft,
      name,
      dose: (draft.dose || "").trim(),
      frequency: (draft.frequency || "").trim(),
      ...(!sameMedication
        ? { rxCUI: "", ndc: "", productStrength: "", form: "", route: "" }
        : {}),
    });
  };

  return (
    <Drawer
      className="ph-no-capture"
      anchor="right"
      open
      onClose={onClose}
      sx={zIndex ? { zIndex } : undefined}
      PaperProps={{
        role: "dialog",
        "aria-modal": true,
        "aria-labelledby": "medication-details-heading",
        sx: {
          width: { xs: "100%", sm: 460 },
          maxWidth: "100%",
          color: kickNavy,
          fontFamily:
            '"BasisGrotesquePro-Regular", Helvetica, Arial, sans-serif',
          "& .MuiButton-root, & .MuiInputBase-root": { fontFamily: "inherit" },
        },
      }}
    >
      <Box
        component="form"
        onSubmit={save}
        sx={{ display: "flex", flexDirection: "column", minHeight: "100%" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, p: 3 }}>
          <Typography
            component="h2"
            id="medication-details-heading"
            sx={{ ...medicationHeadingStyles, flex: 1 }}
          >
            {isNew ? "Add a medication" : "Medication details"}
          </Typography>
          <IconButton
            aria-label="Close medication details"
            onClick={onClose}
            sx={{ color: kickNavy, width: 44, height: 44 }}
          >
            <CloseRounded />
          </IconButton>
        </Box>
        <Box sx={{ display: "grid", gap: 3, px: 3, pb: 3 }}>
          <Typography sx={{ color: "#626176", fontSize: "15px" }}>
            Add the details you know. It’s OK to leave the dose and frequency
            blank.
          </Typography>
          {[
            ["name", "Medication or supplement name", "e.g. Vitamin D"],
            ["dose", "Dose (optional)", "e.g. 25 mg or 1 tablet"],
            [
              "frequency",
              "How often (optional)",
              "e.g. Once a day or as needed",
            ],
          ].map(([field, label, placeholder], index) => (
            <TextField
              key={field}
              id={`medication-detail-${field}`}
              label={label}
              placeholder={placeholder}
              value={draft[field] || ""}
              onChange={(event) =>
                setDraft({ ...draft, [field]: event.target.value })
              }
              autoFocus={index === (isNew ? 0 : 1)}
              fullWidth
              required={field === "name"}
              inputProps={{ maxLength: 300 }}
              sx={medicationInputStyles}
            />
          ))}
          {sameMedication && productDetails.length > 0 && (
            <Box sx={{ p: 2, backgroundColor: "#faf9fc", borderRadius: "6px" }}>
              <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 0.5 }}>
                Previously reported product details
              </Typography>
              <Typography sx={{ fontSize: "14px", color: "#626176" }}>
                {productDetails.join(" · ")}
              </Typography>
            </Box>
          )}
        </Box>
        <Box
          sx={{
            display: "grid",
            gap: 1.5,
            mt: "auto",
            p: 3,
            pb: "max(24px, env(safe-area-inset-bottom))",
            borderTop: "1px solid #e9e6ef",
            backgroundColor: "#fff",
          }}
        >
          <Button
            type="submit"
            variant="contained"
            disableElevation
            disabled={!name}
            sx={medicationPrimaryButtonStyles}
          >
            {isNew ? "Add medication" : "Save changes"}
          </Button>
          {!isNew && onRemove && (
            <Button
              type="button"
              variant="outlined"
              onClick={onRemove}
              sx={medicationSecondaryButtonStyles}
            >
              Remove this medication
            </Button>
          )}
          <Button
            type="button"
            onClick={onClose}
            sx={medicationSecondaryButtonStyles}
          >
            Cancel
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
